import { useState } from "react";
import {
  X,
  Plus,
  LayoutTemplate,
  Search,
  Filter,
  Star,
  StarOff,
  Trash2,
  Edit2,
  Heart,
  Globe,
  ClipboardCheck,
  Calendar,
  Users,
  FolderCog,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getWorkoutImage } from "@/utils/workoutImages";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import WorkoutForm from "./WorkoutForm";
import TemplatePickerDialog from "./TemplatePickerDialog";
import WeeklyWorkoutPlanView from "./WeeklyWorkoutPlanView";

const difficultyColors = {
  beginner: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  intermediate: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  advanced: "bg-purple-500/10 text-purple-400 border-purple-500/20",
};

const goalLabels = {
  strength: "Strength",
  hypertrophy: "Hypertrophy",
  endurance: "Endurance",
  weight_loss: "Weight Loss",
  general_fitness: "General Fitness",
};

export default function PlanManagementModal({ open, onClose, defaultTab = "my-plans" }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showForm, setShowForm] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // Search & Filters for My Plans
  const [search, setSearch] = useState("");
  const [filterDifficulty, setFilterDifficulty] = useState("all");
  const [filterGoal, setFilterGoal] = useState("all");

  // Search & Filters for Community Plans
  const [communitySearch, setCommunitySearch] = useState("");
  const [communityFilterDifficulty, setCommunityFilterDifficulty] = useState("all");
  const [communityFilterGoal, setCommunityFilterGoal] = useState("all");

  const { data: workouts = [] } = useQuery({
    queryKey: ["workouts"],
    queryFn: () => base44.entities.WorkoutPlan.list(),
  });

  const { data: savedPlans = [] } = useQuery({
    queryKey: ["savedPlans"],
    queryFn: () => base44.entities.SavedPlan.filter({ user_id: user?.id, plan_type: "workout" }),
    enabled: !!user,
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.WorkoutPlan.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workouts"] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.WorkoutPlan.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workouts"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.WorkoutPlan.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workouts"] }),
  });

  const savePlanMutation = useMutation({
    mutationFn: async ({ planId, isSaved }) => {
      if (isSaved) {
        const existing = savedPlans.find((s) => s.plan_id === planId);
        if (existing) await base44.entities.SavedPlan.delete(existing.id);
      } else {
        await base44.entities.SavedPlan.create({ user_id: user.id, plan_id: planId, plan_type: "workout" });
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["savedPlans"] }),
  });

  const isSaved = (planId) => savedPlans.some((s) => s.plan_id === planId);

  const myPlans = workouts.filter(
    (w) => w.created_by_id === user?.id || w.assigned_to_user_id === user?.id
  );
  const communityPlans = workouts.filter((w) => w.visible_on_community === true);

  const applyFilters = (plans, searchVal, diffVal, goalVal) => {
    return plans.filter((p) => {
      const matchSearch =
        !searchVal ||
        p.name.toLowerCase().includes(searchVal.toLowerCase()) ||
        (p.description || "").toLowerCase().includes(searchVal.toLowerCase());
      const matchDiff = diffVal === "all" || p.difficulty === diffVal;
      const matchGoal = goalVal === "all" || p.goal === goalVal;
      return matchSearch && matchDiff && matchGoal;
    });
  };

  const filteredMyPlans = applyFilters(myPlans, search, filterDifficulty, filterGoal);
  const filteredCommunityPlans = applyFilters(
    communityPlans,
    communitySearch,
    communityFilterDifficulty,
    communityFilterGoal
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-5 select-none animate-in fade-in duration-200">
      <div
        className="bg-[#0B1220] border border-white/15 rounded-3xl w-full max-w-4xl max-h-[92dvh] flex flex-col overflow-hidden shadow-2xl relative"
        style={{
          paddingTop: "max(12px, env(safe-area-inset-top))",
          paddingBottom: "max(12px, env(safe-area-inset-bottom))",
        }}
      >
        {/* Modal Top Bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-sky-500/15 border border-sky-400/30 flex items-center justify-center text-sky-400">
              <FolderCog className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white leading-tight">Training Management</h2>
              <p className="text-[11px] text-slate-400">Manage your workout plans, schedules & library</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition active:scale-95"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Modal Body with Tabs */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 scrollbar-thin">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <TabsList className="bg-[#101C2E] border border-white/10 p-1 rounded-xl">
                <TabsTrigger
                  value="my-plans"
                  className="data-[state=active]:bg-sky-500 data-[state=active]:text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
                >
                  My Plans ({myPlans.length})
                </TabsTrigger>
                <TabsTrigger
                  value="community"
                  className="data-[state=active]:bg-sky-500 data-[state=active]:text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
                >
                  Community
                </TabsTrigger>
                <TabsTrigger
                  value="schedule"
                  className="data-[state=active]:bg-sky-500 data-[state=active]:text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
                >
                  Weekly Schedule
                </TabsTrigger>
              </TabsList>

              {activeTab === "my-plans" && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowTemplatePicker(true)}
                    className="border-white/15 bg-white/5 hover:bg-white/10 text-white text-xs gap-1.5 h-8 rounded-lg"
                  >
                    <LayoutTemplate className="w-3.5 h-3.5" /> From Template
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setPrefillData(null);
                      setShowForm(true);
                    }}
                    className="bg-sky-500 hover:bg-sky-400 text-white text-xs font-bold gap-1.5 h-8 rounded-lg shadow-[0_0_15px_rgba(14,165,233,0.3)]"
                  >
                    <Plus className="w-3.5 h-3.5" /> Create Plan
                  </Button>
                </div>
              )}
            </div>

            {/* TAB 1: MY PLANS */}
            <TabsContent value="my-plans" className="space-y-4 m-0">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search my plans..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-[#101C2E] border-white/10 text-white h-9 rounded-xl text-xs"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={filterDifficulty} onValueChange={setFilterDifficulty}>
                    <SelectTrigger className="w-32 bg-[#101C2E] border-white/10 text-xs h-9 rounded-xl text-slate-300">
                      <SelectValue placeholder="Difficulty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Difficulties</SelectItem>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filterGoal} onValueChange={setFilterGoal}>
                    <SelectTrigger className="w-36 bg-[#101C2E] border-white/10 text-xs h-9 rounded-xl text-slate-300">
                      <SelectValue placeholder="Goal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Goals</SelectItem>
                      <SelectItem value="strength">Strength</SelectItem>
                      <SelectItem value="hypertrophy">Hypertrophy</SelectItem>
                      <SelectItem value="endurance">Endurance</SelectItem>
                      <SelectItem value="weight_loss">Weight Loss</SelectItem>
                      <SelectItem value="general_fitness">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {filteredMyPlans.length === 0 ? (
                <div className="text-center py-12 bg-[#101C2E]/40 border border-white/10 rounded-2xl p-6">
                  <p className="text-sm font-semibold text-white mb-1">No workout plans found</p>
                  <p className="text-xs text-slate-400 mb-4">
                    Create your first plan or choose from one of our curated templates.
                  </p>
                  <Button
                    size="sm"
                    onClick={() => {
                      setPrefillData(null);
                      setShowForm(true);
                    }}
                    className="bg-sky-500 hover:bg-sky-400 text-white text-xs font-bold gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" /> Create Plan
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredMyPlans.map((plan) => {
                    const isOwn = plan.created_by_id === user?.id;
                    const isAdmin = user?.role === "admin";
                    const canEdit = isOwn || isAdmin;
                    const isAssignedToMe = plan.assigned_to_user_id === user?.id && !isOwn;

                    return (
                      <Card
                        key={plan.id}
                        className="bg-[#101C2E] border-white/10 shadow-sm overflow-hidden flex flex-col justify-between"
                      >
                        <div className="relative h-20 w-full overflow-hidden bg-slate-900">
                          <img
                            src={getWorkoutImage(plan)}
                            alt={plan.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#101C2E] via-[#101C2E]/50 to-transparent" />
                        </div>
                        <CardHeader className="p-4 pb-2 pt-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <CardTitle className="text-sm font-bold text-white leading-tight">
                                  {plan.name}
                                </CardTitle>
                                {plan.is_active && (
                                  <Badge className="bg-sky-500/20 text-sky-400 border-none text-[9px] px-1.5 py-0">
                                    Active
                                  </Badge>
                                )}
                                {isAssignedToMe && (
                                  <Badge className="bg-amber-500/20 text-amber-400 border-none text-[9px] px-1.5 py-0 gap-1">
                                    <ClipboardCheck className="w-2.5 h-2.5" /> Coach
                                  </Badge>
                                )}
                              </div>
                              <div className="flex gap-1.5 mt-2 flex-wrap">
                                {plan.difficulty && (
                                  <Badge
                                    variant="outline"
                                    className={`text-[9px] capitalize ${
                                      difficultyColors[plan.difficulty] || ""
                                    }`}
                                  >
                                    {plan.difficulty}
                                  </Badge>
                                )}
                                {plan.goal && (
                                  <Badge
                                    variant="outline"
                                    className="text-[9px] border-white/10 text-slate-300"
                                  >
                                    {goalLabels[plan.goal] || plan.goal}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {canEdit && (
                              <div className="flex items-center gap-1 shrink-0">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="w-7 h-7 hover:bg-white/10"
                                  onClick={() => {
                                    setPrefillData(plan);
                                    setShowForm(true);
                                  }}
                                >
                                  <Edit2 className="w-3.5 h-3.5 text-slate-400 hover:text-white" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="w-7 h-7 hover:bg-white/10"
                                  onClick={() =>
                                    updateMutation.mutate({
                                      id: plan.id,
                                      data: { is_active: !plan.is_active },
                                    })
                                  }
                                >
                                  {plan.is_active ? (
                                    <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                                  ) : (
                                    <StarOff className="w-3.5 h-3.5 text-slate-400" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="w-7 h-7 text-rose-400 hover:bg-rose-500/20"
                                  onClick={() => deleteMutation.mutate(plan.id)}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>
                          {plan.description && (
                            <p className="text-xs text-slate-400 line-clamp-2 mt-1.5">
                              {plan.description}
                            </p>
                          )}
                        </CardHeader>

                        <CardContent className="p-4 pt-1">
                          {(plan.exercises || []).length > 0 && (
                            <div className="space-y-1 bg-[#050A12]/60 rounded-xl p-2.5 mt-2">
                              {(plan.exercises || []).slice(0, 3).map((ex, i) => (
                                <div
                                  key={i}
                                  className="flex items-center justify-between text-xs py-0.5 text-slate-300"
                                >
                                  <span className="capitalize truncate max-w-[70%]">
                                    {ex.name}
                                  </span>
                                  <span className="text-slate-400 text-[11px] font-mono">
                                    {ex.sets}×{ex.reps}
                                  </span>
                                </div>
                              ))}
                              {(plan.exercises || []).length > 3 && (
                                <p className="text-[10px] text-sky-400 font-semibold pt-1">
                                  +{plan.exercises.length - 3} more exercises
                                </p>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* TAB 2: COMMUNITY PLANS */}
            <TabsContent value="community" className="space-y-4 m-0">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search community plans..."
                    value={communitySearch}
                    onChange={(e) => setCommunitySearch(e.target.value)}
                    className="pl-9 bg-[#101C2E] border-white/10 text-white h-9 rounded-xl text-xs"
                  />
                </div>
                <div className="flex gap-2">
                  <Select
                    value={communityFilterDifficulty}
                    onValueChange={setCommunityFilterDifficulty}
                  >
                    <SelectTrigger className="w-32 bg-[#101C2E] border-white/10 text-xs h-9 rounded-xl text-slate-300">
                      <SelectValue placeholder="Difficulty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Difficulties</SelectItem>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={communityFilterGoal} onValueChange={setCommunityFilterGoal}>
                    <SelectTrigger className="w-36 bg-[#101C2E] border-white/10 text-xs h-9 rounded-xl text-slate-300">
                      <SelectValue placeholder="Goal" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Goals</SelectItem>
                      <SelectItem value="strength">Strength</SelectItem>
                      <SelectItem value="hypertrophy">Hypertrophy</SelectItem>
                      <SelectItem value="endurance">Endurance</SelectItem>
                      <SelectItem value="weight_loss">Weight Loss</SelectItem>
                      <SelectItem value="general_fitness">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {filteredCommunityPlans.length === 0 ? (
                <div className="text-center py-12 bg-[#101C2E]/40 border border-white/10 rounded-2xl p-6">
                  <Globe className="w-8 h-8 mx-auto mb-2 text-slate-500 opacity-60" />
                  <p className="text-sm font-semibold text-white mb-1">No community plans found</p>
                  <p className="text-xs text-slate-400">Try changing your search terms or filters.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {filteredCommunityPlans.map((plan) => {
                    const isOwn = plan.created_by_id === user?.id;
                    const saved = isSaved(plan.id);

                    return (
                      <Card
                        key={plan.id}
                        className="bg-[#101C2E] border-white/10 shadow-sm overflow-hidden flex flex-col justify-between"
                      >
                        <div className="relative h-20 w-full overflow-hidden bg-slate-900">
                          <img
                            src={getWorkoutImage(plan)}
                            alt={plan.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#101C2E] via-[#101C2E]/50 to-transparent" />
                        </div>
                        <CardHeader className="p-4 pb-2 pt-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <CardTitle className="text-sm font-bold text-white leading-tight">
                                  {plan.name}
                                </CardTitle>
                                <Badge className="bg-sky-500/10 text-sky-300 border-none text-[9px] px-1.5 py-0 gap-1">
                                  <Globe className="w-2.5 h-2.5" /> Community
                                </Badge>
                              </div>
                              <div className="flex gap-1.5 mt-2 flex-wrap">
                                {plan.difficulty && (
                                  <Badge
                                    variant="outline"
                                    className={`text-[9px] capitalize ${
                                      difficultyColors[plan.difficulty] || ""
                                    }`}
                                  >
                                    {plan.difficulty}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {!isOwn && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-8 h-8 hover:bg-white/10"
                                onClick={() =>
                                  savePlanMutation.mutate({ planId: plan.id, isSaved: saved })
                                }
                              >
                                <Heart
                                  className={`w-4 h-4 ${
                                    saved ? "fill-rose-500 text-rose-500" : "text-slate-400"
                                  }`}
                                />
                              </Button>
                            )}
                          </div>
                          {plan.description && (
                            <p className="text-xs text-slate-400 line-clamp-2 mt-1.5">
                              {plan.description}
                            </p>
                          )}
                        </CardHeader>

                        <CardContent className="p-4 pt-1">
                          <div className="flex items-center justify-between text-xs text-slate-400 pt-2 border-t border-white/10">
                            <span>{(plan.exercises || []).length} exercises</span>
                            <span className="capitalize">{goalLabels[plan.goal] || plan.goal}</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* TAB 3: WEEKLY SCHEDULE MANAGER */}
            <TabsContent value="schedule" className="space-y-4 m-0">
              <div className="bg-[#101C2E]/60 border border-white/10 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-sky-400" />
                  <h3 className="text-sm font-bold text-white">Full Weekly Schedule & Assignment</h3>
                </div>
                <WeeklyWorkoutPlanView userId={user?.id} />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Workout Form Dialog */}
        <WorkoutForm
          open={showForm}
          onOpenChange={(v) => {
            setShowForm(v);
            if (!v) setPrefillData(null);
          }}
          onSubmit={(data) =>
            prefillData?.id
              ? updateMutation.mutate({ id: prefillData.id, data })
              : createMutation.mutate(data)
          }
          initialData={prefillData}
        />

        {/* Template Picker Dialog */}
        <TemplatePickerDialog
          open={showTemplatePicker}
          onOpenChange={setShowTemplatePicker}
          onSelect={(template) => {
            setPrefillData({
              name: template.name,
              description: template.description,
              difficulty: template.difficulty,
              goal: template.goal,
              days_per_week: template.days_per_week,
              exercises: template.exercises || [],
            });
            setShowTemplatePicker(false);
            setShowForm(true);
          }}
        />
      </div>
    </div>
  );
}
